// src/pages/Property/reviewPropertyDetails.js
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import VisibilityIcon from '@mui/icons-material/Visibility';
import HomeIcon from '@mui/icons-material/Home';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ApartmentIcon from '@mui/icons-material/Apartment';
import LandscapeIcon from '@mui/icons-material/Landscape';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import ReceiptIcon from '@mui/icons-material/Receipt';
import DescriptionIcon from '@mui/icons-material/Description';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import ContactsIcon from '@mui/icons-material/Contacts';
import DownloadIcon from '@mui/icons-material/Download';
import './property.scss';
import PropertyLayout from './layout';
import useApiState from '../../Hooks/useApiState';
import LoaderOverlay from '../../Components/Loader/LoaderOverlay';
import PropertyConfirmation from './confirmation';
import { fetchFileById } from '../../Api/request/propertyDetail';
import UseAxios from '../../Api/http/http';
import { legalDocumentAPI } from '../../services/api';

// Component to handle image display with file ID
const ReviewImage = React.memo(({ imageData, alt }) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const handleImageDownload = async () => {
    if (downloading) return; // Prevent multiple clicks

    try {
      setDownloading(true);
      console.log('Downloading image:', imageData);

      // If imageData has a direct URL, use it
      if (imageData?.url && (imageData.url.startsWith('http') || imageData.url.startsWith('blob:'))) {
        const downloadLink = document.createElement('a');
        downloadLink.href = imageData.url;
        downloadLink.download = imageData.name || 'property-image.jpg';
        downloadLink.style.display = 'none'; // Hide the link
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        return;
      }

      // If imageData has file_id, fetch the blob first
      const fileId = imageData?.file_id || imageData?.id || imageData;
      if (!fileId) {
        throw new Error('No file ID or URL found for image');
      }

      console.log('Fetching image blob for ID:', fileId);
      const resp = await UseAxios(fetchFileById({ fileId }));
      if (!resp || !resp.data) {
        throw new Error('Failed to fetch image data');
      }

      let blob;
      if (resp.data instanceof Blob) {
        blob = resp.data;
      } else if (resp.data instanceof ArrayBuffer) {
        blob = new Blob([resp.data]);
      } else {
        throw new Error('Invalid image data received');
      }

      // Create download URL from blob
      const downloadUrl = URL.createObjectURL(blob);
      const downloadLink = document.createElement('a');
      downloadLink.href = downloadUrl;
      downloadLink.download = imageData?.name || `property-image-${fileId}.jpg`;
      downloadLink.style.display = 'none'; // Hide the link
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);

      // Clean up the blob URL
      setTimeout(() => {
        URL.revokeObjectURL(downloadUrl);
      }, 1000);

    } catch (error) {
      console.error('Image download failed:', error);
      alert(`Failed to download image: ${error.message}`);
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    let blobUrl;

    const loadImage = async () => {
      // If imageData is already a URL, use it directly
      if (typeof imageData === 'string' && (imageData.startsWith('http') || imageData.startsWith('blob:'))) {
        setImageUrl(imageData);
        return;
      }

      // If imageData has a URL property, use it
      if (imageData?.url) {
        setImageUrl(imageData.url);
        return;
      }

      // If imageData has file_id, fetch the blob
      const fileId = imageData?.file_id || imageData?.id || imageData;
      if (!fileId) {
        setError(true);
        return;
      }

      try {
        setLoading(true);
        const resp = await UseAxios(fetchFileById({ fileId }));
        if (!mounted) return;
        if (!resp || !resp.data) {
          setError(true);
          return;
        }

        let blob;
        if (resp.data instanceof Blob) blob = resp.data;
        else if (resp.data instanceof ArrayBuffer) blob = new Blob([resp.data]);
        else {
          setError(true);
          return;
        }

        blobUrl = URL.createObjectURL(blob);
        setImageUrl(blobUrl);
      } catch (err) {
        console.error('Failed to load image:', err);
        setError(true);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadImage();

    return () => {
      mounted = false;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [imageData]);

  if (loading) {
    return (
      <div style={{ width: 120, height: 90, border: '1px solid #eee', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ fontSize: 12 }}>Loading...</span>
      </div>
    );
  }

  if (error || !imageUrl) {
    return (
      <div style={{ width: 120, height: 90, border: '1px solid #eee', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ fontSize: 12 }}>Image not available</span>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      <img
        src={imageUrl}
        alt={alt}
        className="preview-image"
        style={{
          display: 'block',
          width: 120,
          height: 90,
          objectFit: 'cover',
          borderRadius: 8,
          border: '1px solid #eee',
          margin: '0 auto',
          cursor: downloading ? 'wait' : 'pointer',
          opacity: downloading ? 0.7 : 1,
        }}
        onClick={() => handleImageDownload()}
        title={downloading ? 'Downloading...' : 'Click to download image'}
      />
      {downloading && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          borderRadius: 8,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontSize: '12px',
          fontWeight: 'bold',
        }}>
          Downloading...
        </div>
      )}
    </div>
  );
});

ReviewImage.displayName = 'ReviewImage';
ReviewImage.propTypes = {
  imageData: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  alt: PropTypes.string.isRequired,
  onImageClick: PropTypes.func,
};

function ReviewPropertyDetails({
  propertyData = {},
  step,
  totalSteps,
  systemId,
  onSubmit,
  onBackToDashboard,
  prevStep,
  isView = false,
}) {
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationData, setConfirmationData] = useState(null);
  const [previewImg, setPreviewImg] = useState(null);
  const [downloadingDocId, setDownloadingDocId] = useState(null);
  const {
    isSubmitting,
    submitError,
    clearErrors,
    submitData,
  } = useApiState({
    showToastOnError: true,
    defaultErrorMessage: 'Failed to submit property details',
  });

  // Debug: Log propertyData to understand the structure
  console.log('ReviewPropertyDetails - propertyData:', propertyData);
  console.log('ReviewPropertyDetails - images:', propertyData.images);
  console.log('ReviewPropertyDetails - videos:', propertyData.videos);
  console.log('ReviewPropertyDetails - receipts:', propertyData.receipts);
  console.log('ReviewPropertyDetails - property_images:', propertyData.property_images);
  console.log('ReviewPropertyDetails - property_documents:', propertyData.property_documents);

  const fetchBlobByFileName = async (filename) => {
    if (!filename) return null;

    const payloadCandidates = [
      { filename },
      { fileName: filename },
      { name: filename },
    ];

    for (const payload of payloadCandidates) {
      try {
        const response = await UseAxios(fetchFileById(payload));
        if (response?.data instanceof Blob) return response.data;
        if (response?.data instanceof ArrayBuffer) return new Blob([response.data]);
      } catch (error) {
        // Try next payload candidate.
      }
    }

    return null;
  };

  const handleDocumentDownload = async (doc) => {
    const docId = doc.file_id || doc.id || doc.name || doc.filename || Math.random().toString();

    if (downloadingDocId === docId) return; // Prevent multiple clicks

    try {
      setDownloadingDocId(docId);
      console.log('Downloading document:', doc);

      // If doc has a direct URL, use it
      if (doc.url && (doc.url.startsWith('http') || doc.url.startsWith('blob:'))) {
        const downloadLink = document.createElement('a');
        downloadLink.href = doc.url;
        downloadLink.download = doc.name || 'document';
        downloadLink.style.display = 'none'; // Hide the link
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        return;
      }

      // If doc has file_id, fetch the blob first
      const fileId = doc.file_id || doc.id || doc.fileId || null;
      const filename = doc.filename || doc.name || doc.originalName || null;

      if (fileId) {
        try {
          console.log('Fetching file blob for ID:', fileId);
          const resp = await UseAxios(fetchFileById({ fileId }));

          let blob;
          if (resp?.data instanceof Blob) {
            blob = resp.data;
          } else if (resp?.data instanceof ArrayBuffer) {
            blob = new Blob([resp.data]);
          }

          if (blob && blob.size > 0) {
            const downloadUrl = URL.createObjectURL(blob);
            const downloadLink = document.createElement('a');
            downloadLink.href = downloadUrl;
            downloadLink.download = filename || `document-${fileId}`;
            downloadLink.style.display = 'none';
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);
            setTimeout(() => URL.revokeObjectURL(downloadUrl), 1000);
            return;
          }
        } catch (error) {
          console.warn('Fetch by fileId failed, trying filename fallback', { fileId, error });
        }
      }

      if (filename) {
        const blob = await fetchBlobByFileName(filename);
        if (blob && blob.size > 0) {
          const downloadUrl = URL.createObjectURL(blob);
          const downloadLink = document.createElement('a');
          downloadLink.href = downloadUrl;
          downloadLink.download = filename;
          downloadLink.style.display = 'none';
          document.body.appendChild(downloadLink);
          downloadLink.click();
          document.body.removeChild(downloadLink);
          setTimeout(() => URL.revokeObjectURL(downloadUrl), 1000);
          return;
        }

        try {
          const response = await legalDocumentAPI.fetchDocument(filename);
          let blob = null;
          if (response?.data instanceof Blob) blob = response.data;
          else if (response?.data instanceof ArrayBuffer) blob = new Blob([response.data]);
          if (blob && blob.size > 0) {
            const downloadUrl = URL.createObjectURL(blob);
            const downloadLink = document.createElement('a');
            downloadLink.href = downloadUrl;
            downloadLink.download = filename;
            downloadLink.style.display = 'none';
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);
            setTimeout(() => URL.revokeObjectURL(downloadUrl), 1000);
            return;
          }
        } catch (error) {
          console.warn('legalDocumentAPI.fetchDocument failed', { filename, error });
        }
      }

      throw new Error('No file ID or filename found for document');

    } catch (error) {
      console.error('Download failed:', error);
      alert(`Failed to download document: ${error.message}`);
    } finally {
      setDownloadingDocId(null);
    }
  };

  const handleSubmit = async () => {
    clearErrors();
    try {
      const response = await submitData(async () => {
        // Call the onSubmit prop with 'Submitted' status
        return await onSubmit('Submitted');
      }, {
        successMessage: 'Property submitted successfully',
      });
      console.log('response here', response);
      // Show confirmation screen with the response data
      setConfirmationData(response);
      setShowConfirmation(true);
    } catch (error) {
      console.error('Failed to submit property:', error);
    }
  };

  const handleSaveAsDraft = async () => {
    clearErrors();
    try {
      const response = await submitData(async () => {
        // Call the onSubmit prop with 'Draft' status
        return await onSubmit('Draft');
      }, {
        successMessage: 'Property saved as draft successfully',
      });
      // Show confirmation screen with the response data
      setConfirmationData(response);
      setShowConfirmation(true);
    } catch (error) {
      console.error('Failed to save property as draft:', error);
    }
  };

  // If confirmation screen should be shown, render it instead
  if (showConfirmation && confirmationData) {
    return (
      <PropertyConfirmation
        systemId={confirmationData.data?.property_id || confirmationData.property_id || systemId}
        propertyData={confirmationData.data.data || confirmationData}
        step={step}
        totalSteps={totalSteps}
      />
    );
  }

  console.log('data here', propertyData);
  return (
    <PropertyLayout step={step} totalSteps={totalSteps} systemId={systemId} onBackToDashboard={onBackToDashboard} hideStepper={isView}>
      <LoaderOverlay isLoading={isSubmitting} />
      <div className='card'>
        <h3 className='card-title' style={{ display: 'flex', alignItems: 'center', fontSize: 20, fontWeight: 600, marginBottom: 16 }}>
          <VisibilityIcon style={{ verticalAlign: 'middle', marginRight: 8, fontSize: 24 }} />
          Review Property Details
        </h3>

        {/* Display API errors */}
        {submitError && (
          <div className="error-message" style={{
            padding: '10px',
            marginBottom: '15px',
            backgroundColor: '#ffebee',
            color: '#c62828',
            borderRadius: '4px',
            border: '1px solid #ffcdd2',
          }}>
            {submitError}
          </div>
        )}

        {/* Subheaders */}
        <div style={{ textAlign: 'center', margin: '10px 0 18px 0' }}>
          {isView ? (
            <>
              <div style={{ fontSize: 18, fontWeight: 500, marginBottom: 4 }}>Property Details Overview</div>
              <div style={{ color: '#888', fontSize: 15 }}>View complete property information and documents</div>
            </>
          ) : (
            <>
              <div style={{ fontSize: 18, fontWeight: 500, marginBottom: 4 }}>Review Your Property Details</div>
              <div style={{ color: '#888', fontSize: 15 }}>Please review all information before submitting your property listing</div>
            </>
          )}
        </div>

        {/* Basic Information */}
        <div className='inner-card'>
          <h4 className='inner-card-title' style={{ display: 'flex', alignItems: 'center', fontSize: 17, fontWeight: 600, marginBottom: 12 }}>
            <HomeIcon style={{ marginRight: 8, fontSize: 22, verticalAlign: 'middle' }} /> Basic Information
          </h4>
          <div className='review-grid'>
            <div><label>Property ID</label><p>{systemId || '-'}</p></div>
            <div><label>Property Caption</label><p>{propertyData.caption || '-'}</p></div>
            <div><label>Property Type</label><p>{propertyData.type || '-'}</p></div>
            <div><label>Property Sub-Type</label><p>{propertyData.subType || '-'}</p></div>
            <div><label>Description</label><p>{propertyData.description || '-'}</p></div>
          </div>
        </div>

        {/* Location Details */}
        <div className='inner-card'>
          <h4 className='inner-card-title' style={{ display: 'flex', alignItems: 'center', fontSize: 17, fontWeight: 600, marginBottom: 12 }}>
            <LocationOnIcon style={{ marginRight: 8, fontSize: 22, verticalAlign: 'middle' }} /> Location Details
          </h4>
          <div className='review-grid'>
            <div><label>Address Line 1</label><p>{propertyData.address1 || '-'}</p></div>
            <div><label>Address Line 2</label><p>{propertyData.address2 || '-'}</p></div>
            <div><label>Area</label><p>{propertyData.area || '-'}</p></div>
            <div><label>Taluk</label><p>{propertyData.taluk || '-'}</p></div>
            <div><label>District</label><p>{propertyData.district || '-'}</p></div>
            <div><label>City</label><p>{propertyData.city || '-'}</p></div>
            <div><label>Pincode</label><p>{propertyData.pin || '-'}</p></div>
            <div><label>Country</label><p>{propertyData.country || '-'}</p></div>
            <div><label>Jurisdiction</label><p>{propertyData.jurisdiction || '-'}</p></div>
          </div>
        </div>

        {/* Building/Land Details */}
        {(propertyData.subType === 'Villa' || propertyData.subType === 'Complex' || propertyData.subType === 'Flat') && (
          <div className='inner-card'>
            <h4 className='inner-card-title' style={{ display: 'flex', alignItems: 'center', fontSize: 17, fontWeight: 600, marginBottom: 12 }}>
              <ApartmentIcon style={{ marginRight: 8, fontSize: 22, verticalAlign: 'middle' }} /> Building Details
            </h4>
            <div className='review-grid'>
              <div><label>Construction Stage</label><p>{propertyData.constructionStage || '-'}</p></div>
              <div><label>Carpet Area (Sq Ft)</label><p>{propertyData.carpetArea || '-'}</p></div>
              <div><label>Built-up Area (Sanctioned) (Sq Ft)</label><p>{propertyData.builtUpSanctioned || '-'}</p></div>
              <div><label>Built-up Area (Actual) (Sq Ft)</label><p>{propertyData.builtUpActual || '-'}</p></div>
              <div><label>No. of Floors</label><p>{propertyData.noOfFloors || '-'}</p></div>
              <div><label>No. of Parking</label><p>{propertyData.noOfParking || '-'}</p></div>
            </div>
          </div>
        )}
        {(propertyData.subType === 'Land' || propertyData.subType === 'Plot') && (
          <div className='inner-card'>
            <h4 className='inner-card-title' style={{ display: 'flex', alignItems: 'center', fontSize: 17, fontWeight: 600, marginBottom: 12 }}>
              <LandscapeIcon style={{ marginRight: 8, fontSize: 22, verticalAlign: 'middle' }} /> Land Details
            </h4>
            <div className='review-grid'>
              <div><label>Extent (Sq Ft)</label><p>{propertyData.extent || '-'}</p></div>
              <div><label>Unit</label><p>{propertyData.unit || '-'}</p></div>
              <div><label>Patta Extent (Sq Ft)</label><p>{propertyData.pattaExtent || '-'}</p></div>
              <div><label>Kumki Extent (Sq Ft)</label><p>{propertyData.kumkiExtent || '-'}</p></div>
              <div><label>Boundary Type</label><p>{propertyData.boundaryType || '-'}</p></div>
              <div><label>Road Access</label><p>{propertyData.roadAccess ? 'Yes' : 'No'}</p></div>
              <div><label>Road Type</label><p>{propertyData.roadType || '-'}</p></div>
              <div><label>Access Road Width (Ft)</label><p>{propertyData.accessRoadWidth || '-'}</p></div>
              <div><label>Facing</label><p>{propertyData.facing ? propertyData.facing.join(', ') : '-'}</p></div>
              <div><label>Water Source</label><p>{propertyData.waterSource ? propertyData.waterSource.join(', ') : '-'}</p></div>
              <div><label>Power Supply</label><p>{propertyData.powerSupply ? 'Yes' : 'No'}</p></div>
              <div><label>Sanctioned Capacity</label><p>{propertyData.sanctionedCapacity || '-'}</p></div>
              <div><label>Sewage Line</label><p>{propertyData.sewageLine ? 'Yes' : 'No'}</p></div>
              <div><label>Sewage Type</label><p>{propertyData.sewageType || '-'}</p></div>
            </div>
          </div>
        )}

        {/* Images and Videos */}
        <div className='inner-card'>
          <h4 className='inner-card-title' style={{ display: 'flex', alignItems: 'center', fontSize: 17, fontWeight: 600, marginBottom: 12 }}>
            <PhotoLibraryIcon style={{ marginRight: 8, fontSize: 22, verticalAlign: 'middle' }} /> Property Images & Videos
          </h4>
          <div className='review-grid'>
            {(propertyData.images && propertyData.images.length > 0) ||
             (propertyData.property_images && propertyData.property_images.length > 0) ||
             (propertyData.videos && propertyData.videos.length > 0) ||
             (propertyData.property_videos && propertyData.property_videos.length > 0) ? (
                <div className="preview-container" style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
                  {/* Display Images from propertyData.images */}
                  {propertyData.images && propertyData.images.map((img, idx) => {
                    const imageName = img?.name || `Property Image ${idx + 1}`;

                    return (
                      <div
                        key={`img-${idx}`}
                        className="preview-wrapper"
                        style={{ position: 'relative', width: 130, marginBottom: 16 }}
                      >
                        <ReviewImage
                          imageData={img}
                          alt={imageName}
                          onImageClick={setPreviewImg}
                        />
                        <div className="preview-label" style={{ textAlign: 'center', fontSize: 12, marginTop: 4, wordBreak: 'break-all' }}>
                          {imageName}
                        </div>
                      </div>
                    );
                  })}

                  {/* Display Images from propertyData.property_images if images not available */}
                  {(!propertyData.images || propertyData.images.length === 0) &&
                  propertyData.property_images && propertyData.property_images.map((img, idx) => {
                    const imageName = img?.name || `Property Image ${idx + 1}`;

                    return (
                      <div
                        key={`prop-img-${idx}`}
                        className="preview-wrapper"
                        style={{ position: 'relative', width: 130, marginBottom: 16 }}
                      >
                        <ReviewImage
                          imageData={img}
                          alt={imageName}
                          onImageClick={setPreviewImg}
                        />
                        <div className="preview-label" style={{ textAlign: 'center', fontSize: 12, marginTop: 4, wordBreak: 'break-all' }}>
                          {imageName}
                        </div>
                      </div>
                    );
                  })}

                  {/* Display Videos */}
                  {propertyData.videos && propertyData.videos.map((vid, idx) => {
                    const videoUrl = vid?.url || vid?.file || (typeof vid === 'string' ? vid : null);
                    const videoName = vid?.name || `Property Video ${idx + 1}`;

                    return (
                      <div
                        key={`vid-${idx}`}
                        className="preview-wrapper"
                        style={{ position: 'relative', width: 130, marginBottom: 16 }}
                      >
                        <video
                          src={videoUrl}
                          className="preview-image"
                          style={{
                            display: 'block',
                            width: 120,
                            height: 90,
                            objectFit: 'cover',
                            borderRadius: 8,
                            border: '1px solid #eee',
                            margin: '0 auto',
                          }}
                          controls
                          onError={(errorEvent) => {
                            errorEvent.target.style.display = 'none';
                            errorEvent.target.nextSibling.textContent = 'Video failed to load';
                          }}
                        />
                        <div className="preview-label" style={{ textAlign: 'center', fontSize: 12, marginTop: 4, wordBreak: 'break-all' }}>
                          {videoName}
                        </div>
                      </div>
                    );
                  })}

                  {/* Display Videos from propertyData.property_videos if videos not available */}
                  {(!propertyData.videos || propertyData.videos.length === 0) &&
                  propertyData.property_videos && propertyData.property_videos.map((vid, idx) => {
                    const videoUrl = vid?.url || vid?.file || (typeof vid === 'string' ? vid : null);
                    const videoName = vid?.name || `Property Video ${idx + 1}`;

                    return (
                      <div
                        key={`prop-vid-${idx}`}
                        className="preview-wrapper"
                        style={{ position: 'relative', width: 130, marginBottom: 16 }}
                      >
                        <video
                          src={videoUrl}
                          className="preview-image"
                          style={{
                            display: 'block',
                            width: 120,
                            height: 90,
                            objectFit: 'cover',
                            borderRadius: 8,
                            border: '1px solid #eee',
                            margin: '0 auto',
                          }}
                          controls
                          onError={(errorEvent) => {
                            errorEvent.target.style.display = 'none';
                            errorEvent.target.nextSibling.textContent = 'Video failed to load';
                          }}
                        />
                        <div className="preview-label" style={{ textAlign: 'center', fontSize: 12, marginTop: 4, wordBreak: 'break-all' }}>
                          {videoName}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{
                  textAlign: 'center',
                  color: '#666',
                  padding: '20px',
                  border: '1px dashed #ccc',
                  borderRadius: '4px',
                }}>
                  No images or videos uploaded
                </div>
              )}
          </div>
        </div>

        {/* Image Preview Modal */}
        {previewImg && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              background: 'rgba(0,0,0,0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999,
            }}
            onClick={() => setPreviewImg(null)}
          >
            <div style={{ position: 'relative' }} onClick={event => event.stopPropagation()}>
              <img src={previewImg} alt="Preview" style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: 8 }} />
              <button
                style={{
                  position: 'absolute',
                  top: 10,
                  right: 10,
                  background: 'rgba(255,255,255,0.8)',
                  border: 'none',
                  borderRadius: '50%',
                  width: 30,
                  height: 30,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                onClick={() => setPreviewImg(null)}
              >
                ✕
              </button>
            </div>
          </div>
        )}

        {/* Tax Details */}
        <div className='inner-card'>
          <h4 className='inner-card-title' style={{ display: 'flex', alignItems: 'center', fontSize: 17, fontWeight: 600, marginBottom: 12 }}>
            <ReceiptIcon style={{ marginRight: 8, fontSize: 22, verticalAlign: 'middle' }} /> Tax Details
          </h4>
          <div className='review-grid'>
            <div><label>Tax Paid Up To</label><p>{propertyData.taxPaidUpTo || '-'}</p></div>
            <div><label>Tax Receipt Exists</label><p>{propertyData.taxReceiptExist ? 'Yes' : 'No'}</p></div>
            <div><label>Receipts</label><p>{propertyData.receipts && propertyData.receipts.length > 0 ? propertyData.receipts.map((receipt, idx) => {
              console.log(`Tax receipt ${idx}:`, receipt);
              return (
                <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                  <a
                    href="#"
                    onClick={(event) => {
                      event.preventDefault();
                      handleDocumentDownload(receipt);
                    }}
                    style={{
                      textDecoration: 'underline',
                      color: downloadingDocId === (receipt.file_id || receipt.id || receipt.name) ? '#999' : '#1976d2',
                      cursor: downloadingDocId === (receipt.file_id || receipt.id || receipt.name) ? 'wait' : 'pointer',
                      opacity: downloadingDocId === (receipt.file_id || receipt.id || receipt.name) ? 0.6 : 1,
                    }}
                  >
                    {downloadingDocId === (receipt.file_id || receipt.id || receipt.name) ? 'Downloading...' : (receipt.name || `Tax Receipt ${idx + 1}`)}
                  </a>
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleDocumentDownload(receipt);
                    }}
                    disabled={downloadingDocId === (receipt.file_id || receipt.id || receipt.name)}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: downloadingDocId === (receipt.file_id || receipt.id || receipt.name) ? 'wait' : 'pointer',
                      color: downloadingDocId === (receipt.file_id || receipt.id || receipt.name) ? '#999' : '#1976d2',
                      padding: '2px',
                      display: 'flex',
                      alignItems: 'center',
                      opacity: downloadingDocId === (receipt.file_id || receipt.id || receipt.name) ? 0.6 : 1,
                    }}
                    title="Download document"
                  >
                    <DownloadIcon fontSize='small' />
                  </button>
                </div>
              );
            }) : '-'}</p></div>
          </div>
        </div>

        {/* Uploaded Documents */}
        <div className='inner-card'>
          <h4 className='inner-card-title' style={{ display: 'flex', alignItems: 'center', fontSize: 17, fontWeight: 600, marginBottom: 12 }}>
            <DescriptionIcon style={{ marginRight: 8, fontSize: 22, verticalAlign: 'middle' }} /> Uploaded Documents
          </h4>
          <div className='review-grid'>
            {/* Main Document Types */}
            {[
              'Tax Receipt',
              'Sale Deed',
              'e-Khata',
              'FMB / Nakshe',
              'Conversion Order/Copy',
              'EC',
              'RTC',
              'Mutation',
              'Occupancy Certificate',
              'Original Mother Deed',
              'Building Licence',
              'CRZ NOC',
              'RERA Certificate',
              'Pollution Control Board',
              'Fire Dept Noc',
              'Airport Clearance',
              'Sanction Plan Of Building',
              'Bank documents (if loans)',
              'Legal documents (if litigation)',
              'Electricity Bill',
              'Rent agreements',
              'Survey settlement records',
              'Possession certificate',
              'NOC from society builder',
              'Insurance documents',
            ].map((docKey) => {
              // Handle special case for Tax Receipt which might be stored as 'receipts'
              const docData = docKey === 'Tax Receipt' ? propertyData.receipts : propertyData[docKey];
              console.log(`Document type: ${docKey}, data:`, docData);
              return (
                <div key={docKey}>
                  <label>{docKey}</label>
                  <p>{docData && docData.length > 0 ? docData.map((doc, idx) => {
                    console.log(`${docKey} document ${idx}:`, doc);
                    return (
                      <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <a
                          href="#"
                          onClick={(event) => {
                            event.preventDefault();
                            handleDocumentDownload(doc);
                          }}
                          style={{
                            textDecoration: 'underline',
                            color: downloadingDocId === (doc.file_id || doc.id || doc.name) ? '#999' : '#1976d2',
                            cursor: downloadingDocId === (doc.file_id || doc.id || doc.name) ? 'wait' : 'pointer',
                            opacity: downloadingDocId === (doc.file_id || doc.id || doc.name) ? 0.6 : 1,
                          }}
                        >
                          {downloadingDocId === (doc.file_id || doc.id || doc.name) ? 'Downloading...' : (doc.name || `${docKey} ${idx + 1}`)}
                        </a>
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleDocumentDownload(doc);
                          }}
                          disabled={downloadingDocId === (doc.file_id || doc.id || doc.name)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: downloadingDocId === (doc.file_id || doc.id || doc.name) ? 'wait' : 'pointer',
                            color: downloadingDocId === (doc.file_id || doc.id || doc.name) ? '#999' : '#1976d2',
                            padding: '2px',
                            display: 'flex',
                            alignItems: 'center',
                            opacity: downloadingDocId === (doc.file_id || doc.id || doc.name) ? 0.6 : 1,
                          }}
                          title="Download document"
                        >
                          <DownloadIcon fontSize='small' />
                        </button>
                      </div>
                    );
                  }) : '-'}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Commercial Details */}
        <div className='inner-card'>
          <h4 className='inner-card-title' style={{ display: 'flex', alignItems: 'center', fontSize: 17, fontWeight: 600, marginBottom: 12 }}>
            <BusinessCenterIcon style={{ marginRight: 8, fontSize: 22, verticalAlign: 'middle' }} /> Commercial Details
          </h4>
          <div className='review-grid'>
            <div><label>Purchase Date</label><p>{propertyData.purchaseDate || '-'}</p></div>
            <div><label>Purchase Price</label><p>{propertyData.price ? `₹ ${Number(propertyData.price).toLocaleString('en-IN')}` : '-'}</p></div>
            <div><label>Estimated Current Value</label><p>{propertyData.estimatedValue ? `₹ ${Number(propertyData.estimatedValue).toLocaleString('en-IN')}` : '-'}</p></div>
          </div>
        </div>

        {/* Contact Details */}
        <div className='inner-card'>
          <h4 className='inner-card-title' style={{ display: 'flex', alignItems: 'center', fontSize: 17, fontWeight: 600, marginBottom: 12 }}>
            <ContactsIcon style={{ marginRight: 8, fontSize: 22, verticalAlign: 'middle' }} /> Contact Details
          </h4>
          <div className='review-grid'>
            <div><label>Owner Name</label><p>{propertyData.contactName || '-'}</p></div>
            <div><label>Owner Phone</label><p>{propertyData.phoneNumber ? `+91 ${propertyData.phoneNumber}` : '-'}</p></div>
            <div><label>Owner Email</label><p>{propertyData.email || '-'}</p></div>
            {!propertyData.caretakerIsOwner && (
              <>
                <div><label>Caretaker Name</label><p>{propertyData.caretakerName || '-'}</p></div>
                <div><label>Caretaker Phone</label><p>{propertyData.caretakerPhoneNumber ? `+91 ${propertyData.caretakerPhoneNumber}` : '-'}</p></div>
                <div><label>Caretaker Email</label><p>{propertyData.caretakerEmail || '-'}</p></div>
              </>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className='form-actions'>
          <button type='button' className='btn secondary' onClick={prevStep} disabled={isSubmitting}>
            Previous
          </button>
          {!isView && (
            <div style={{ display: 'flex', gap: '12px' }}>
              <button type='button' className='btn secondary' onClick={handleSaveAsDraft} disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : 'Save as Draft'}
              </button>
              <button type='button' className='btn primary' onClick={handleSubmit} disabled={isSubmitting}>
                {isSubmitting ? 'Submitting...' : 'Submit Property Listing'}
              </button>
            </div>
          )}
        </div>
      </div>
    </PropertyLayout>
  );
}

ReviewPropertyDetails.propTypes = {
  propertyData: PropTypes.object,
  step: PropTypes.number.isRequired,
  totalSteps: PropTypes.number.isRequired,
  systemId: PropTypes.string,
  onSubmit: PropTypes.func,
  onBackToDashboard: PropTypes.func,
  prevStep: PropTypes.func,
  isView: PropTypes.bool,
};

ReviewPropertyDetails.defaultProps = {
  propertyData: {},
  systemId: '',
  onSubmit: () => Promise.resolve({ property_id: 'TEST001', data: {} }),
  prevStep: () => { },
};

export default ReviewPropertyDetails;
